import { useEffect, useState } from 'react';
import { ArrowUp } from 'lucide-react';

// Back to top button, global floating action.
//
// Appears bottom-right on every page once the user has scrolled
// past the threshold. Smooth-scrolls back to the top on click.
// Works on desktop AND mobile (some browsers were showing a
// system-native cue only on mobile, this is the consistent
// in-app equivalent).
//
// Z-index sits below the PWA install prompt (z-[60]) so we never
// stack the two on top of each other.
const SCROLL_THRESHOLD_PX = 500;

export function BackToTop() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > SCROLL_THRESHOLD_PX);
    // Run once on mount in case the page loaded already scrolled
    // (e.g. browser back-navigation restoring scroll position).
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleClick = () => {
    // Respect prefers-reduced-motion users with an instant jump
    // instead of a 1.5-second smooth scroll that can trigger
    // vestibular issues.
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({
      top: 0,
      behavior: reducedMotion ? 'auto' : 'smooth',
    });
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label="Back to top"
      title="Back to top"
      className={[
        // Layout
        'fixed bottom-5 right-5 sm:bottom-7 sm:right-7 z-[55]',
        'h-12 w-12 sm:h-13 sm:w-13 rounded-full',
        'flex items-center justify-center',
        // Style, gold pill with subtle ring matching the brand
        'bg-[#1A1A1A] text-[#D4AF37] shadow-[0_8px_24px_-6px_rgba(0,0,0,0.45)]',
        'ring-1 ring-[#D4AF37]/30 hover:ring-[#D4AF37]/60',
        'hover:bg-[#D4AF37] hover:text-[#1A1A1A]',
        'active:scale-95',
        // Motion
        'transition-all duration-300 ease-out',
        visible
          ? 'opacity-100 pointer-events-auto translate-y-0'
          : 'opacity-0 pointer-events-none translate-y-3',
      ].join(' ')}
      data-testid="back-to-top"
    >
      <ArrowUp className="h-5 w-5" strokeWidth={2} />
    </button>
  );
}
